/**
 * 상세 화면이 알게 된 Probe 변화를 목록에 알린다.
 *
 * 목록은 Probe 를 따로 묻지 않고 Dataset 응답의 `latest_probe` 를 보인다. 그래서 상세 창에서 Probe 를 요청하거나
 * 폴링이 상태를 바꿔도 목록은 다시 받기 전까지 모른다. 상세 화면이 바뀐 Probe 를 이 통로로 알리고 목록이 그 행을
 * 고친다. 같은 페이지 안의 통로라 다른 브라우저 탭에는 닿지 않는다.
 *
 * Owns:
 * - 알림 통로와 Dataset 에 반영하는 규칙(`withProbeChange`).
 *
 * Does not own:
 * - Probe 요청 · 폴링 — `datasetDetailController.ts`. 목록 갱신 — `page/DatasetTableSource.ts`.
 */
import type { Dataset, LatestProbe } from "../domain/dataset";

export type ProbeChange = {
  readonly datasetUuid: string;
  readonly probe: LatestProbe;
  /**
   * 접수 응답이면 `true` 다. 그 Probe 는 새 건이거나 진행 중이던 건이라 가장 최근 Probe 다. 폴링 결과(`false`)는
   * 목록이 보이는 Probe 와 같은 건일 때만 반영한다.
   */
  readonly requested: boolean;
};

type Listener = (change: ProbeChange) => void;

const listeners = new Set<Listener>();

export function publishProbeChange(change: ProbeChange): void {
  for (const listener of listeners) {
    listener(change);
  }
}

export function subscribeProbeChanges(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 변화를 반영한 Dataset. 이 Dataset 의 변화가 아니거나 반영할 것이 없으면 같은 객체를 돌려준다. */
export function withProbeChange(dataset: Dataset, change: ProbeChange): Dataset {
  if (dataset.uuid !== change.datasetUuid) {
    return dataset;
  }
  const current = dataset.latestProbe;
  if (!change.requested && current?.uuid !== change.probe.uuid) {
    return dataset;
  }
  if (
    current !== null &&
    current.uuid === change.probe.uuid &&
    current.state === change.probe.state &&
    current.failureCode === change.probe.failureCode
  ) {
    return dataset;
  }
  return { ...dataset, latestProbe: change.probe };
}
